<template>
  <AgentLayout>
    <div class="mx-auto w-full max-w-6xl px-4 pb-16 sm:px-8">
      <header class="border-b border-gray-100 pb-6">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#4D2542]">Humaniz University</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-gray-900">Your courses</h1>
        <p class="mt-1 text-gray-500">Everything real estate, on demand. Pick up where you left off.</p>
      </header>

      <div v-if="courses.length" class="mt-10 flex flex-col gap-8">
        <router-link
          v-for="course in courses"
          :key="course.id"
          :to="{ name: 'University Course', params: { slug: course.slug } }"
          class="group grid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
        >
          <div class="relative min-h-[14rem] overflow-hidden bg-[#202F7E]">
            <div
              class="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              :style="course.featured_image_url ? { backgroundImage: `url('${course.featured_image_url}')` } : {}"
            ></div>
            <div class="absolute inset-0 bg-gradient-to-tr from-[#202F7E]/70 via-[#4D2542]/30 to-transparent"></div>
            <span
              class="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#4D2542] backdrop-blur"
            >
              Course
            </span>
          </div>

          <div class="flex flex-col justify-center gap-4 p-7 sm:p-9">
            <h2 class="text-2xl font-bold leading-snug tracking-tight text-gray-900 sm:text-3xl">
              {{ course.name }}
            </h2>
            <p class="line-clamp-3 text-[15px] leading-relaxed text-gray-600">{{ course.description }}</p>

            <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-gray-500">
              <span class="flex items-center gap-2">
                <vue-feather type="layers" size="16" class="text-[#4D2542]" />
                {{ course.sections_count }} {{ course.sections_count === 1 ? 'module' : 'modules' }}
              </span>
              <span class="flex items-center gap-2">
                <vue-feather type="play-circle" size="16" class="text-[#4D2542]" />
                {{ course.lectures_count }} {{ course.lectures_count === 1 ? 'lesson' : 'lessons' }}
              </span>
            </div>

            <div class="max-w-sm">
              <CourseProgress :completed="course.completed_lectures_count" :total="course.lectures_count" />
            </div>

            <span
              class="mt-2 inline-flex w-fit items-center gap-2 rounded-lg bg-[#202F7E] px-5 py-2.5 text-sm font-semibold text-white transition-colors group-hover:bg-[#19246F]"
            >
              {{ ctaLabel(course) }}
              <vue-feather type="arrow-right" size="16" class="transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </router-link>
      </div>

      <div v-else class="mt-10 flex flex-col gap-8">
        <div class="grid overflow-hidden rounded-2xl border border-gray-200 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <Skeleton class="min-h-[14rem]" />
          <div class="flex flex-col gap-4 p-9">
            <Skeleton class="h-8 w-3/4" />
            <Skeleton class="h-4 w-full" />
            <Skeleton class="h-4 w-5/6" />
            <Skeleton class="mt-2 h-10 w-40" />
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { Api } from '@/api';
import AgentLayout from '@/components/layouts/AgentLayout/index.vue';
import { Skeleton } from '@/components/ui/skeleton';
import CourseProgress from '@/components/agent/university/CourseProgress.vue';
import type { CourseListItem } from '@/api/agent/university/course';

const courses = ref<CourseListItem[]>([]);

const ctaLabel = (course: CourseListItem) => {
  if (course.completed_lectures_count === 0) return 'Start learning';
  if (course.completed_lectures_count >= course.lectures_count) return 'Review course';
  return 'Resume';
};

const loadCourses = async () => {
  const response = await Api.AgentUniversityCourse.listCourses();
  if (response.status === 'success') {
    courses.value = response.data;
  }
};

onMounted(loadCourses);
</script>
